import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';
import PromoCodePopup from './PromoCodePopup';

const AuthPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [tradeRequirements, setTradeRequirements] = useState(null);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  const [idVerificationStatus, setIdVerificationStatus] = useState(null); // Track ID verification status
  const [error, setError] = useState(''); // Error state for form validation
  
  // Promo popup states
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  const [promoUserInfo, setPromoUserInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('signin');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { toast } = useToast();

  // Get URL parameters
  const redirectPath = searchParams.get('redirect');
  const selectedPlan = searchParams.get('plan');

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  const [signupForm, setSignupForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'customer', // customer or provider
    phone: '',
    company: '',
    license: '',
    selectedTrade: '',
    experience: '',
    location: '',
    idVerificationDocument: '', // URL to uploaded ID document
    idVerificationFile: null, // File object to upload
    idVerificationPreview: null, // Preview URL for display
    promoCode: '', // Promo code for service providers
    promoCodeValid: null, // null, true, or false
    promoCodeDetails: null, // Details about the promo code
    acceptedTerms: false  // Add T&C acceptance field
  });

  // Load trade requirements when component mounts
  useEffect(() => {
    loadTradeRequirements();
  }, []);

  // Handle URL parameters for subscription flow
  useEffect(() => {
    if (redirectPath === 'subscription' && selectedPlan) {
      // If coming from subscription page, switch to signup tab and set to provider
      setActiveTab('signup');
      setSignupForm(prev => ({
        ...prev,
        userType: 'provider'
      }));
      
      // Show a message about the selected plan
      toast({
        title: "Subscription Plan Selected",
        description: `You've selected the ${selectedPlan.replace('_', ' ')} plan. Complete signup to proceed to payment.`,
        duration: 5000,
      });
    }
  }, [redirectPath, selectedPlan, toast]);

  const loadTradeRequirements = async () => {
    setLoadingTrades(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/trade-requirements`);
      
      if (response.ok) {
        const data = await response.json();
        setTradeRequirements(data);
      }
    } catch (error) {
      console.error('Error loading trade requirements:', error);
    } finally {
      setLoadingTrades(false);
    }
  };

  const getSelectedTradeRequirements = () => {
    if (!tradeRequirements || !signupForm.selectedTrade) return null;
    
    const trade = tradeRequirements.trades.find(t => t.trade === signupForm.selectedTrade);
    return trade;
  };

  const isLicenseRequired = () => {
    const tradeInfo = getSelectedTradeRequirements();
    return tradeInfo?.requiresLicense || false;
  };

  const handleIdVerificationUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please select a JPEG, PNG, WebP image or PDF document.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select a file smaller than 10MB.",
        variant: "destructive",
      });
      return;
    }

    setUploadingId(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Note: We can't upload without being authenticated, so we'll store the file
      // and upload it after successful registration
      const reader = new FileReader();
      reader.onload = (e) => {
        setSignupForm({
          ...signupForm, 
          idVerificationFile: file,
          idVerificationPreview: e.target.result
        });
      };
      reader.readAsDataURL(file);

      toast({
        title: "ID Document Selected",
        description: "Your ID document will be uploaded after registration.",
      });
    } catch (error) {
      console.error('Error selecting ID document:', error);
      toast({
        title: "Error",
        description: "Failed to select ID document. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploadingId(false);
    }
  };

  const validatePromoCode = async (promoCode, email) => {
    if (!promoCode.trim() || !email.trim()) {
      setSignupForm(prev => ({
        ...prev,
        promoCodeValid: null,
        promoCodeDetails: null
      }));
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/validate-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          promoCode: promoCode.trim(),
          email: email.trim()
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSignupForm(prev => ({
          ...prev,
          promoCodeValid: true,
          promoCodeDetails: data
        }));
        
        toast({
          title: "Valid Promo Code!",
          description: `${data.description} - ${data.trialDurationDays} days free trial`,
        });
      } else {
        const errorData = await response.json();
        setSignupForm(prev => ({
          ...prev,
          promoCodeValid: false,
          promoCodeDetails: null
        }));
        
        toast({
          title: "Invalid Promo Code",
          description: errorData.detail || "Please check your promo code and try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setSignupForm(prev => ({
        ...prev,
        promoCodeValid: false,
        promoCodeDetails: null
      }));
      
      toast({
        title: "Error",
        description: "Failed to validate promo code. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePromoCodeChange = (e) => {
    const newPromoCode = e.target.value;
    setSignupForm(prev => ({
      ...prev,
      promoCode: newPromoCode
    }));

    // Auto-validate when both promo code and email are present
    if (newPromoCode.trim() && signupForm.email.trim()) {
      // Debounce validation
      clearTimeout(window.promoValidationTimeout);
      window.promoValidationTimeout = setTimeout(() => {
        validatePromoCode(newPromoCode, signupForm.email);
      }, 500);
    } else {
      setSignupForm(prev => ({
        ...prev,
        promoCodeValid: null,
        promoCodeDetails: null
      }));
    }
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setSignupForm(prev => ({
      ...prev,
      email: newEmail
    }));

    // Re-validate promo code if it exists
    if (signupForm.promoCode.trim() && newEmail.trim()) {
      clearTimeout(window.promoValidationTimeout);
      window.promoValidationTimeout = setTimeout(() => {
        validatePromoCode(signupForm.promoCode, newEmail);
      }, 500);
    }
  };

  const handleLogin = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🔍 handleLogin triggered');
    setIsLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      console.log('🔍 Login attempt:', {
        backendUrl,
        email: loginForm.email,
        passwordLength: loginForm.password.length
      });

      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginForm),
      });

      console.log('🔍 Login response status:', response.status);

      const data = await response.json();
      console.log('🔍 Login response data:', data);

      if (response.ok) {
        login(data.user);
        localStorage.setItem('dcTech_token', data.token);
        toast({
          title: "Welcome back!",
          description: "You've successfully signed in to D.C. Technology Corp.",
        });
        console.log('✅ Login successful, navigating based on user type');
        
        // Small delay to ensure user state is set before navigation
        setTimeout(() => {
          // Route based on user type
          if (data.user.userType === 'admin') {
            console.log('🔄 Admin user - redirecting to admin dashboard');
            navigate('/admin');
          } else if (data.user.userType === 'supervisor') {
            console.log('🔄 Supervisor user - redirecting to supervisor dashboard');
            navigate('/supervisor-dashboard');
          } else {
            console.log('🔄 Regular user - redirecting to dashboard');
            navigate('/dashboard');
          }
        }, 100);
      } else {
        console.error('❌ Login failed:', data);
        toast({
          title: "Login failed",
          description: data.message || "Invalid email or password.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('❌ Login network error:', error);
      toast({
        title: "Login failed",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Comprehensive validation with detailed error messages
    const validationErrors = [];

    // Basic required field validation
    if (!signupForm.name || signupForm.name.trim().length < 2) {
      validationErrors.push("Full name is required (minimum 2 characters)");
    }

    if (!signupForm.email || !signupForm.email.includes('@')) {
      validationErrors.push("Valid email address is required");
    }

    if (!signupForm.password || signupForm.password.length < 6) {
      validationErrors.push("Password must be at least 6 characters long");
    }

    if (signupForm.password !== signupForm.confirmPassword) {
      validationErrors.push("Passwords do not match");
    }

    if (!signupForm.acceptedTerms) {
      validationErrors.push("You must accept the Terms and Conditions to create an account");
    }

    // ID verification validation (required for all users)
    if (!signupForm.idVerificationFile) {
      validationErrors.push("ID verification document is required (government-issued ID or utility bill)");
    }

    // Provider-specific validation
    if (signupForm.userType === 'provider') {
      if (!signupForm.selectedTrade) {
        validationErrors.push("Trade selection is required for service providers");
      }

      // License validation for licensed trades
      if (isLicenseRequired() && (!signupForm.license || signupForm.license.trim().length < 3)) {
        validationErrors.push(`Professional license number is required for ${signupForm.selectedTrade} (minimum 3 characters)`);
      }

      if (!signupForm.company || signupForm.company.trim().length < 2) {
        validationErrors.push("Company/Business name is required (minimum 2 characters)");
      }

      if (!signupForm.location || signupForm.location.trim().length < 3) {
        validationErrors.push("Service area/location is required (minimum 3 characters)");
      }

      if (!signupForm.phone || signupForm.phone.trim().length < 10) {
        validationErrors.push("Phone number is required for service providers (minimum 10 digits)");
      }
    }

    // If there are validation errors, show them all
    if (validationErrors.length > 0) {
      toast({
        title: "Account Creation Failed",
        description: (
          <div className="space-y-1">
            <p className="font-semibold">Please fix the following issues:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        ),
        variant: "destructive",
        duration: 8000, // Show for longer to read all errors
      });
      setIsLoading(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      // Use the new registration endpoint that supports promo codes
      const endpoint = signupForm.promoCode && signupForm.userType === 'provider' 
        ? `${backendUrl}/api/auth/register-with-promo`
        : `${backendUrl}/api/auth/register`;
      
      const registrationData = {
        name: signupForm.name,
        email: signupForm.email,
        password: signupForm.password,
        userType: signupForm.userType,
        phone: signupForm.phone,
        company: signupForm.company,
        license: signupForm.license,
        selectedTrade: signupForm.selectedTrade,
        experience: signupForm.experience,
        location: signupForm.location,
        acceptedTerms: signupForm.acceptedTerms  // Add T&C acceptance to backend data
      };
      
      // Add promo code if provided and user is a provider
      if (signupForm.promoCode && signupForm.userType === 'provider') {
        registrationData.promoCode = signupForm.promoCode;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const data = await response.json();

      if (response.ok) {
        // Check if this is the new email verification flow
        if (data.verificationRequired) {
          // New email verification flow - show success message and ask to check email
          toast({
            title: "Registration Successful! 📧",
            description: `Account created for ${data.email}. Please check your email and click the verification link to activate your account.`,
            duration: 8000,
          });
          
          // Clear form
          setSignupForm({
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            userType: 'customer',
            phone: '',
            company: '',
            license: '',
            selectedTrade: '',
            experience: '',
            location: '',
            idVerificationDocument: '',
            idVerificationFile: null,
            idVerificationPreview: null,
            promoCode: '',
            promoCodeValid: null,
            promoCodeDetails: null,
            acceptedTerms: false
          });
          
          // Switch to login tab
          setActiveTab('login');
          
          return; // Exit here for email verification flow
        }
        
        // Legacy flow (if any users still need immediate login)
        login(data.user);
        localStorage.setItem('dcTech_token', data.token);
        
        // Upload ID verification document after successful registration
        if (signupForm.idVerificationFile) {
          try {
            const formData = new FormData();
            formData.append('file', signupForm.idVerificationFile);

            const uploadResponse = await fetch(`${backendUrl}/api/upload/id-verification`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${data.token}`,
              },
              body: formData,
            });

            if (uploadResponse.ok) {
              const uploadData = await uploadResponse.json();
              console.log('ID verification document uploaded:', uploadData.url);
              
              // Perform name verification on the uploaded ID
              try {
                const verificationResponse = await fetch(`${backendUrl}/api/verify-id-name`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${data.token}`,
                  },
                  body: formData, // Reuse the same formData with the ID file
                });

                if (verificationResponse.ok) {
                  const verificationData = await verificationResponse.json();
                  setIdVerificationStatus(verificationData);
                  
                  if (verificationData.verification_status === 'verified') {
                    toast({
                      title: "ID Verification Complete",
                      description: `Name verified with ${verificationData.confidence_score.toFixed(1)}% confidence`,
                    });
                  } else if (verificationData.verification_status === 'partial_match') {
                    toast({
                      title: "ID Verification - Manual Review",
                      description: `Partial name match detected (${verificationData.confidence_score.toFixed(1)}% confidence). Account created successfully.`,
                      variant: "destructive",
                    });
                  } else {
                    toast({
                      title: "ID Verification Failed",
                      description: `Name on ID does not match profile (${verificationData.confidence_score.toFixed(1)}% confidence). Please contact support if this is incorrect.`,
                      variant: "destructive",
                    });
                  }
                } else {
                  console.error('Name verification failed');
                  toast({
                    title: "ID Name Verification",
                    description: "Could not verify name on ID document. Account created successfully.",
                    variant: "destructive",
                  });
                }
              } catch (verificationError) {
                console.error('Error during name verification:', verificationError);
                // Don't block registration if verification fails
              }
            } else {
              console.error('Failed to upload ID verification document');
              // Don't block registration if ID upload fails
            }
          } catch (uploadError) {
            console.error('Error uploading ID verification:', uploadError);
            // Don't block registration if ID upload fails
          }
        }

        toast({
          title: "Registration Successful!",
          description: `Welcome ${data.user.name}! ${data.user.userType === 'provider' 
            ? 'Redirecting to complete your subscription...' 
            : 'You can now book services and shop for supplies.'}`,
        });

        // Redirect based on user type and origin
        if (data.user.userType === 'provider') {
          // Check if promo popup should be shown
          const promoShown = await triggerPromoPopup(data.user);
          
          if (!promoShown) {
            // No promo popup, proceed with normal redirect
            if (redirectPath === 'subscription' && selectedPlan) {
              // Coming from subscription page - redirect back with user now logged in
              navigate(`/subscription?plan=${selectedPlan}&signup=complete`);
            } else {
              navigate('/subscription');
            }
          }
          // If promo popup is shown, redirect will happen in handlePromoPopupClose
        } else {
          navigate('/dashboard');
        }
      } else {
        // Handle different types of errors with specific messages
        let errorMessage = data.message || data.detail || "Failed to create account.";
        
        // Make error messages more user-friendly
        if (errorMessage.includes("already exists") || errorMessage.includes("already registered")) {
          errorMessage = `⚠️ Email Already Used: ${errorMessage}`;
        } else if (errorMessage.includes("license")) {
          errorMessage = `📋 License Issue: ${errorMessage}`;
        } else if (errorMessage.includes("trade")) {
          errorMessage = `🔧 Trade Selection: ${errorMessage}`;
        }
        
        toast({
          title: "Registration Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 8000, // Show longer for complex error messages
        });
      }
    } catch (error) {
      toast({
        title: "Registration failed",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setIsLoading(true);
    // Redirect to Google OAuth endpoint
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
  };

  // Check if promo feature is enabled
  const checkPromoFeature = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/promo-settings`);
      if (response.ok) {
        const settings = await response.json();
        return settings.featureEnabled;
      }
    } catch (error) {
      console.error('Error checking promo feature:', error);
    }
    return false; // Default to disabled if check fails
  };

  // Show promo popup for providers
  const triggerPromoPopup = async (user) => {
    if (user.userType === 'provider') {
      const promoEnabled = await checkPromoFeature();
      if (promoEnabled) {
        setPromoUserInfo({
          name: user.name,
          email: user.email
        });
        setShowPromoPopup(true);
        return true; // Popup will be shown
      }
    }
    return false; // No popup needed
  };

  const handlePromoGenerated = (promoData) => {
    console.log('Promo code generated:', promoData);
    toast({
      title: "Promo Code Sent! 🎉",
      description: `Your ${promoData.durationMonths}-month free Basic plan code has been sent to your email.`,
    });
  };

  const handlePromoPopupClose = () => {
    setShowPromoPopup(false);
    setPromoUserInfo(null);
    
    // Continue with normal redirect
    const searchParams = new URLSearchParams(location.search);
    const redirectPath = searchParams.get('redirect');
    const selectedPlan = searchParams.get('plan');
    
    if (redirectPath === 'subscription' && selectedPlan) {
      navigate(`/subscription?plan=${selectedPlan}&signup=complete`);
    } else {
      navigate('/subscription');
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setError(''); // Clear errors when switching tabs
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900 flex items-center justify-center p-3 sm:p-4 md:p-6">
      <div className="w-full max-w-sm sm:max-w-md">
        {/* Back Button */}
        <div className="mb-4">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-white hover:bg-white/10 flex items-center space-x-2"
          >
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M10 19l-7-7m0 0l7-7m-7 7h18" 
              />
            </svg>
            <span>Back</span>
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-3 sm:mb-4">
            <div className="text-2xl sm:text-3xl md:text-4xl">⚡</div>
            <span className="text-lg sm:text-2xl md:text-3xl font-bold text-white">D.C. Technology Corp.</span>
          </div>
          <p className="text-white/80 text-sm sm:text-base md:text-lg">Your electrical marketplace</p>
        </div>

        <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-2xl">
          <CardHeader className="pb-4 sm:pb-6">
            <CardTitle className="text-center text-white text-lg sm:text-xl md:text-2xl">Join D.C. Technology Corp.</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-slate-800/80 h-10 sm:h-12">
                <TabsTrigger 
                  value="signin" 
                  className="text-white text-xs sm:text-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white hover:bg-slate-700/80 transition-colors"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="signup" 
                  className="text-white text-xs sm:text-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white hover:bg-slate-700/80 transition-colors"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="email" className="text-white text-sm sm:text-base">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="user@example.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                      className="bg-white/10 border-white/30 text-white placeholder-white/50 text-sm sm:text-base py-2 sm:py-3 min-h-[44px]"
                      required
                    />
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="password" className="text-white text-sm sm:text-base">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                      className="bg-white/10 border-white/30 text-white placeholder-white/50 text-sm sm:text-base py-2 sm:py-3 min-h-[44px]"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500   text-white font-semibold py-2 sm:py-3 text-sm sm:text-base min-h-[44px]"
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing In..." : "Sign In"}
                  </Button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-white/30" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-transparent px-2 text-white/70">Or continue with</span>
                  </div>
                </div>

                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full border-white/50 text-white bg-white/10"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {isLoading ? "Connecting..." : "Google Sign In"}
                </Button>
              </TabsContent>

              <TabsContent value="signup" className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                <form onSubmit={handleSignup} className="space-y-3 sm:space-y-4" noValidate>
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="userType" className="text-white text-sm sm:text-base">Account Type</Label>
                    <div className="flex gap-1 sm:gap-2">
                      <Badge 
                        variant={signupForm.userType === 'customer' ? 'default' : 'outline'}
                        className={`cursor-pointer px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm min-h-[36px] flex items-center ${
                          signupForm.userType === 'customer' 
                            ? 'bg-blue-500 text-white' 
                            : 'border-white/50 text-white bg-white/10'
                        }`}
                        onClick={() => setSignupForm({...signupForm, userType: 'customer'})}
                      >
                        Customer
                      </Badge>
                      <Badge 
                        variant={signupForm.userType === 'provider' ? 'default' : 'outline'}
                        className={`cursor-pointer px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm min-h-[36px] flex items-center ${
                          signupForm.userType === 'provider' 
                            ? 'bg-purple-500 text-white' 
                            : 'border-white/50 text-white bg-white/10'
                        }`}
                        onClick={() => setSignupForm({...signupForm, userType: 'provider'})}
                      >
                        Service Provider
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="name" className="text-white text-sm sm:text-base">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={signupForm.name}
                      onChange={(e) => setSignupForm({...signupForm, name: e.target.value})}
                      className="bg-white/10 border-white/30 text-white placeholder-white/50 text-sm sm:text-base py-2 sm:py-3 min-h-[44px]"
                    />
                  </div>

                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="signupEmail" className="text-white text-sm sm:text-base">Email</Label>
                    <Input
                      id="signupEmail"
                      type="email"
                      placeholder="john@example.com"
                      value={signupForm.email}
                      onChange={handleEmailChange}
                      className="bg-white/10 border-white/30 text-white placeholder-white/50 text-sm sm:text-base py-2 sm:py-3 min-h-[44px]"
                    />
                  </div>

                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="signupPassword" className="text-white text-sm sm:text-base">Password</Label>
                    <Input
                      id="signupPassword"
                      type="password"
                      placeholder="Create password"
                      value={signupForm.password}
                      onChange={(e) => setSignupForm({...signupForm, password: e.target.value})}
                      className="bg-white/10 border-white/30 text-white placeholder-white/50 text-sm sm:text-base py-2 sm:py-3 min-h-[44px]"
                    />
                  </div>

                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="confirmPassword" className="text-white text-sm sm:text-base">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                      value={signupForm.confirmPassword}
                      onChange={(e) => setSignupForm({...signupForm, confirmPassword: e.target.value})}
                      className="bg-white/10 border-white/30 text-white placeholder-white/50 text-sm sm:text-base py-2 sm:py-3 min-h-[44px]"
                    />
                  </div>

                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="phone" className="text-white text-sm sm:text-base">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={signupForm.phone}
                      onChange={(e) => setSignupForm({...signupForm, phone: e.target.value})}
                      className="bg-white/10 border-white/30 text-white placeholder-white/50 text-sm sm:text-base py-2 sm:py-3 min-h-[44px]"
                    />
                  </div>

                  {/* Promo Code Section for Service Providers */}
                  {signupForm.userType === 'provider' && (
                    <div className="space-y-2">
                      <Label htmlFor="promoCode" className="text-white">
                        Promo Code (Optional)
                        <span className="text-white/70 text-sm block font-normal mt-1">
                          Enter your promo code for free trial benefits
                        </span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="promoCode"
                          type="text"
                          placeholder="Enter promo code"
                          value={signupForm.promoCode}
                          onChange={handlePromoCodeChange}
                          className={`bg-white/10 border-white/30 text-white placeholder-white/50 ${
                            signupForm.promoCodeValid === true 
                              ? 'border-green-400/50' 
                              : signupForm.promoCodeValid === false 
                                ? 'border-red-400/50' 
                                : ''
                          }`}
                        />
                        {signupForm.promoCodeValid === true && (
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <span className="text-green-400">✓</span>
                          </div>
                        )}
                        {signupForm.promoCodeValid === false && (
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <span className="text-red-400">✗</span>
                          </div>
                        )}
                      </div>
                      {signupForm.promoCodeValid === true && signupForm.promoCodeDetails && (
                        <div className="bg-green-600/20 border border-green-400/30 rounded-lg p-3">
                          <p className="text-green-300 text-sm">
                            🎉 <strong>Promo Applied:</strong> {signupForm.promoCodeDetails.description}
                          </p>
                          <p className="text-green-200 text-xs mt-1">
                            You'll get {signupForm.promoCodeDetails.trialDurationDays} days of free access!
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ID Verification Upload Section */}
                  <div className="space-y-4">
                    <Label className="text-white text-lg">
                      Proof of Identity & Address *
                      <span className="text-white/70 text-sm block font-normal mt-1">
                        Upload a government-issued ID or utility bill to verify your identity
                      </span>
                    </Label>
                    <div className="space-y-2">
                      {signupForm.idVerificationPreview && (
                        <div className="mb-3">
                          <div className="w-full max-w-sm border border-white/30 rounded-lg overflow-hidden">
                            {signupForm.idVerificationFile?.type === 'application/pdf' ? (
                              <div className="p-4 bg-white/10 text-center">
                                <div className="text-white text-2xl mb-2">📄</div>
                                <p className="text-white/80 text-sm">PDF Document Selected</p>
                                <p className="text-white/60 text-xs">{signupForm.idVerificationFile?.name}</p>
                              </div>
                            ) : (
                              <img 
                                src={signupForm.idVerificationPreview} 
                                alt="ID Preview" 
                                className="w-full h-32 object-cover"
                              />
                            )}
                            {/* ID Verification Status Indicator */}
                            {idVerificationStatus && (
                              <div className={`p-2 text-center text-xs ${
                                idVerificationStatus.verification_status === 'verified' 
                                  ? 'bg-green-600/20 text-green-300'
                                  : idVerificationStatus.verification_status === 'partial_match'
                                  ? 'bg-yellow-600/20 text-yellow-300'
                                  : 'bg-red-600/20 text-red-300'
                              }`}>
                                {idVerificationStatus.verification_status === 'verified' && '✅ Name Verified'}
                                {idVerificationStatus.verification_status === 'partial_match' && '⚠️ Partial Match - Review Required'}
                                {idVerificationStatus.verification_status === 'no_match' && '❌ Name Does Not Match'}
                                <div className="text-xs opacity-75">
                                  Confidence: {idVerificationStatus.confidence_score?.toFixed(1)}%
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <input
                        type="file"
                        id="idVerification"
                        accept="image/*,.pdf"
                        onChange={handleIdVerificationUpload}
                        className="hidden"
                        disabled={uploadingId}
                      />
                      <label
                        htmlFor="idVerification"
                        className={`inline-flex items-center px-4 py-2 border border-white/30 rounded-md text-white bg-white/10 bg-white/10 cursor-pointer transition-colors w-full justify-center ${
                          uploadingId ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {uploadingId ? 'Processing...' : 
                         signupForm.idVerificationPreview ? 'Change Document' : 'Choose Document'}
                      </label>
                      <p className="text-white/60 text-sm">
                        Accepted: Driver's License, Passport, State ID, Utility Bill (JPEG, PNG, PDF - Max 10MB)
                      </p>
                    </div>
                  </div>

                  {signupForm.userType === 'provider' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="selectedTrade" className="text-white">Primary Trade/Specialty *</Label>
                        <select
                          id="selectedTrade"
                          value={signupForm.selectedTrade}
                          onChange={(e) => setSignupForm({...signupForm, selectedTrade: e.target.value})}
                          className="w-full bg-white/10 border-white/30 text-white rounded-md px-3 py-2 focus:border-white/50"
                        >
                          <option value="" className="text-gray-800">Select your primary trade</option>
                          {loadingTrades ? (
                            <option className="text-gray-800">Loading trades...</option>
                          ) : (
                            tradeRequirements?.trades.map((trade) => (
                              <option key={trade.trade} value={trade.trade} className="text-gray-800">
                                {trade.trade} {trade.requiresLicense ? '(License Required)' : ''}
                              </option>
                            ))
                          )}
                        </select>
                        {signupForm.selectedTrade && (
                          <p className="text-white/70 text-sm">
                            {getSelectedTradeRequirements()?.description}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-white">Company/Business Name *</Label>
                        <Input
                          id="company"
                          type="text"
                          placeholder="Your Professional Company"
                          value={signupForm.company}
                          onChange={(e) => setSignupForm({...signupForm, company: e.target.value})}
                          className="bg-white/10 border-white/30 text-white placeholder-white/50"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="license" className="text-white">
                          Professional License Number {isLicenseRequired() ? '*' : '(Optional)'}
                        </Label>
                        <Input
                          id="license"
                          type="text"
                          placeholder={isLicenseRequired() ? "Required for this trade" : "License number if applicable"}
                          value={signupForm.license}
                          onChange={(e) => setSignupForm({...signupForm, license: e.target.value})}
                          className={`bg-white/10 border-white/30 text-white placeholder-white/50 ${
                            isLicenseRequired() ? 'border-yellow-400/50' : ''
                          }`}
                        />
                        {isLicenseRequired() && (
                          <p className="text-yellow-300 text-sm flex items-center">
                            ⚠️ Professional license required for this trade
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="experience" className="text-white">Years of Experience</Label>
                        <Input
                          id="experience"
                          type="text"
                          placeholder="e.g., 10 years, 5+ years"
                          value={signupForm.experience}
                          onChange={(e) => setSignupForm({...signupForm, experience: e.target.value})}
                          className="bg-white/10 border-white/30 text-white placeholder-white/50"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="location" className="text-white">Service Area/Location *</Label>
                        <Input
                          id="location"
                          type="text"
                          placeholder="e.g., Chicago, IL or Service Area"
                          value={signupForm.location}
                          onChange={(e) => setSignupForm({...signupForm, location: e.target.value})}
                          className="bg-white/10 border-white/30 text-white placeholder-white/50"
                        />
                      </div>

                      <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-3">
                        <p className="text-blue-300 text-sm">
                          📋 <strong>Next Step:</strong> After registration, you'll need to subscribe to our professional plan to start offering services and bidding on jobs.
                        </p>
                      </div>
                    </>
                  )}

                  {/* Terms and Conditions Acceptance - Required for all users */}
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="acceptedTerms"
                        checked={signupForm.acceptedTerms}
                        onChange={(e) => setSignupForm({...signupForm, acceptedTerms: e.target.checked})}
                        className="mt-1 h-4 w-4 text-blue-600 bg-white/10 border-white/30 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <label htmlFor="acceptedTerms" className="text-white text-sm leading-relaxed">
                        I have read, understood, and agree to be bound by the{' '}
                        <a 
                          href="/terms-and-conditions" 
                          className="text-blue-300  underline font-medium"
                        >
                          Terms and Conditions
                        </a>
                        {' '}and acknowledge that these terms may be updated by D.C. Technology Corp. at any time. *
                      </label>
                    </div>
                    <div className="bg-yellow-600/20 border border-yellow-400/30 rounded-lg px-3 py-2">
                      <p className="text-yellow-200 text-xs">
                        ⚠️ <strong>Required:</strong> You must accept our Terms and Conditions to create an account. 
                        These terms include important information about your rights, responsibilities, and platform usage.
                      </p>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-purple-500 to-teal-500   text-white font-semibold py-2 sm:py-3 text-sm sm:text-base min-h-[44px]"
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating Account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            
            {/* Footer with Terms and Support Links - Moved to bottom of form */}
            <div className="mt-6 pt-4 border-t border-white/20 text-center">
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4 text-white/60 text-sm">
                <a 
                  href="/terms-and-conditions" 
                  className=" transition-colors"
                >
                  Terms and Conditions
                </a>
                <span className="hidden sm:inline">|</span>
                <a 
                  href="/privacy-policy" 
                  className=" transition-colors"
                >
                  Privacy Policy
                </a>
                <span className="hidden sm:inline">|</span>
                <div className="relative group">
                  <button className=" transition-colors focus:outline-none">
                    Contact Support
                  </button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible  bg-black/95 backdrop-blur-lg rounded-lg p-3 min-w-52 border border-white/30 shadow-xl z-50">
                    <div className="space-y-2">
                      <a 
                        href="mailto:Corporate.dccorp@gmail.com?subject=Support Request&body=Hello D.C. Technology Corp. Support Team,%0D%0A%0D%0AI need assistance with:%0D%0A%0D%0A[Please describe your issue here]%0D%0A%0D%0AThank you!" 
                        className="flex items-center space-x-2 text-white/90  bg-white/10 transition-colors p-2 rounded block"
                      >
                        <span>📧</span>
                        <span>Default Email App</span>
                      </a>
                      <a 
                        href="https://mail.google.com/mail/?view=cm&fs=1&to=Corporate.dccorp@gmail.com&su=Support%20Request&body=Hello%20D.C.%20Technology%20Corp.%20Support%20Team,%0D%0A%0D%0AI%20need%20assistance%20with:%0D%0A%0D%0A[Please%20describe%20your%20issue%20here]%0D%0A%0D%0AThank%20you!" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-white/90  bg-white/10 transition-colors p-2 rounded block"
                      >
                        <span>📮</span>
                        <span>Open Gmail</span>
                      </a>
                    </div>
                    {/* Arrow pointing down */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/95"></div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promo Code Popup */}
      {showPromoPopup && promoUserInfo && (
        <PromoCodePopup
          isOpen={showPromoPopup}
          onClose={handlePromoPopupClose}
          userInfo={promoUserInfo}
          onPromoGenerated={handlePromoGenerated}
        />
      )}
    </div>
  );
};

export default AuthPage;